import React, { useEffect, useState } from "react";
import { OperatorEmblem } from "./OperatorEmblem";
import { loadoutIsEmpty } from "@/lib/loadout";

/**
 * Reveal card for a single operator.
 * During cinematic rolling, the `operator` prop is swapped rapidly by the parent,
 * so we just render it directly (slot-machine feel). When rolling ends, we run
 * a decode-text animation on the final name.
 */
export const OperatorCard = ({ operator, side, rolling, rollMode = "cinematic", loadout = null }) => {
  const [decoded, setDecoded] = useState("");

  // Decode animation runs only on the FINAL reveal (not during cinematic cycling).
  useEffect(() => {
    if (!operator || rolling) {
      setDecoded("");
      return;
    }
    if (rollMode === "instant") {
      setDecoded(operator.name.toUpperCase());
      return;
    }
    const target = operator.name.toUpperCase();
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$#@";
    let i = 0;
    const id = setInterval(() => {
      i++;
      let out = "";
      for (let k = 0; k < target.length; k++) {
        if (k < i) out += target[k];
        else if (target[k] === " ") out += " ";
        else out += chars[Math.floor(Math.random() * chars.length)];
      }
      setDecoded(out);
      if (i >= target.length) clearInterval(id);
    }, 40);
    return () => clearInterval(id);
  }, [operator, rolling, rollMode]);

  const accent = side === "attacker" ? "#E67300" : "#1F88E8";
  const sideLabel = side === "attacker" ? "ATTACKER" : "DEFENDER";

  if (!operator && !rolling) {
    return (
      <div
        data-testid="operator-card-empty"
        className="border border-neutral-800 bg-neutral-950/60 p-10 sm:p-16 flex flex-col items-center justify-center min-h-[360px]"
      >
        <div className="text-neutral-600 text-xs tracking-[0.3em] uppercase font-bold">
          Awaiting Roll
        </div>
        <div
          className="mt-4 text-neutral-500 text-center max-w-sm"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Select a side below to randomise an operator.
        </div>
      </div>
    );
  }

  // Cinematic rolling: show the currently-swapped operator directly.
  // Instant rolling: same, but we fade the new op in (very short).
  const nameDisplay = rolling
    ? (operator?.name || "").toUpperCase()
    : decoded || operator?.name;

  // Visual treatment while rolling
  const emblemAnim =
    rolling && rollMode === "cinematic"
      ? "animate-pulse scale-[0.97]"
      : rolling && rollMode === "instant"
        ? "scale-[0.98] opacity-80"
        : "scale-100 opacity-100";

  return (
    <div
      data-testid="operator-card"
      className="relative border bg-neutral-950/80 p-6 sm:p-10 min-h-[360px] transition-all duration-200"
      style={{
        borderColor: accent,
        boxShadow: `inset 0 0 0 1px ${accent}22, 0 0 0 1px ${accent}22`,
      }}
    >
      {/* Corner brackets */}
      <span className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2" style={{ borderColor: accent }} />
      <span className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2" style={{ borderColor: accent }} />
      <span className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2" style={{ borderColor: accent }} />
      <span className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2" style={{ borderColor: accent }} />

      {/* Scanline sweep during cinematic roll */}
      {rolling && rollMode === "cinematic" && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px] opacity-60"
          style={{
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            animation: "r6-scan 0.8s linear infinite",
          }}
        />
      )}

      <div className="flex flex-col sm:flex-row items-center gap-8">
        <div
          className={`shrink-0 transition-transform duration-150 ${emblemAnim}`}
          key={operator?.id /* forces re-mount → restart any emblem transition */}
        >
          <OperatorEmblem id={operator?.id} name={operator?.name || "??"} side={side} size={160} />
        </div>

        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div
            className="text-xs font-bold tracking-[0.3em] mb-2"
            style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
            data-testid="operator-side-label"
          >
            {sideLabel}
          </div>
          <h2
            className="text-white text-4xl sm:text-6xl font-black uppercase tracking-tight leading-none break-words"
            style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            data-testid="operator-name"
          >
            {nameDisplay}
          </h2>

          <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
            <span
              className="px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase border"
              style={{
                borderColor: `${accent}55`,
                color: accent,
                fontFamily: '"JetBrains Mono", monospace',
              }}
              data-testid="operator-unit"
            >
              {operator?.unit}
            </span>
            <span
              className="px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase border border-neutral-700 text-neutral-400"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
              data-testid="operator-role"
            >
              {operator?.role}
            </span>
          </div>

          {/* Compact loadout strip — labelled rows */}
          {!rolling && !loadoutIsEmpty(loadout) && (
            <div
              data-testid="single-loadout-strip"
              className="mt-4 pt-4 border-t space-y-1.5 text-center sm:text-left"
              style={{ borderColor: `${accent}33` }}
            >
              {[
                { label: "PRIMARY", value: loadout?.primary },
                { label: "SECONDARY", value: loadout?.secondary },
                { label: "GADGET", value: loadout?.gadget },
              ]
                .filter((r) => r.value)
                .map((r) => (
                  <div
                    key={r.label}
                    data-testid={`loadout-row-${r.label.toLowerCase()}`}
                    className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3"
                  >
                    <span
                      className="text-[10px] tracking-[0.3em] font-bold shrink-0 sm:w-24"
                      style={{
                        color: accent,
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    >
                      {r.label}
                    </span>
                    <span
                      className="text-white text-sm sm:text-base font-semibold"
                      style={{ fontFamily: '"JetBrains Mono", monospace' }}
                    >
                      {r.value}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperatorCard;
